/*------------------------------------------------------------------------------
   About      : Insight authoring form (epic DA-697, II-FE-3)

                Mockups: `image.png` (header, state banner, action row),
                `image-1.png` (Content & Logic), `image-2.png` (template text).

                Serves both create and edit. In create mode there is no
                `documentId`, no `code` and no `insightId` — quill derives all
                three on save and they are then read-only for life, so the
                Technical Identification panel only appears once the record
                exists.

                The four-branch state banner and the per-field modified
                highlight follow `RecoEditFormComponent`; the save/submit pair
                follows `config-registry/WorkflowActionButtons`.

                ⚠️ `templateString`, `templateExample` and `alternativeVersions`
                are i18n-localized on quill, but `updateAll` has no equivalent of
                the reco path's `updateLocalisedContent`, so only the default
                locale is written and a non-`en` edit is silently dropped (open
                on DA-2369). `TemplateTextEditor` shows the locale it is editing
                and lists the pilot's other locales **disabled**, with the reason
                on each. That is the only shape this control may take until
                DA-2369 lands: one that let an author type into `fr` would lose
                the work without ever saying so.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
/* Section order: published version → classification → criteria → template text
   and AI assistant → validation tiles → related recommendations. The criteria
   builder sits above the template text because what it constrains has to be
   referenced there, and the validation tiles sit directly under the text and
   the AI assistant they report on — reco tagging is a relation rather than
   content, so it closes the form rather than interrupting that chain. */
import { ReactNode, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Divider, Grid, Paper,
         Stack, TextField, Typography } from '@mui/material'
import RestoreIcon     from '@mui/icons-material/Restore'
import DeleteIcon      from '@mui/icons-material/DeleteOutline'
import StorageIcon     from '@mui/icons-material/Storage'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import VisibilityIcon  from '@mui/icons-material/VisibilityOutlined'

import { CONTENT_EDIT_STATUS } from 'types/cms/frontendTypes'
import { UI_PAGES }            from 'framework/access/UiRegistry'
import { BFF_FEATURES }        from 'framework/access/BffFeatureCatalog'
import { ANALYTICS_EVENTS }    from 'framework/analytics/AnalyticsEvents'
import { PermissionGate }      from 'components/access/PermissionGate'
import { useGlobalConfig }     from 'context/GlobalContext'

import { InsightCatalog,
         InsightCreateData,
         InsightRecord }              from '../types/insightTypes'
import { modifiedFields }             from '../utils/insightDiff'
import { InsightClassificationForm }  from './InsightClassificationForm'
import { TemplateTextEditor }         from './TemplateTextEditor'
import { AiWritingAssistantDialog }   from './AiWritingAssistantDialog'
import { TemplateValidationPanel }    from './TemplateValidationPanel'
import type { ValidationGate }       from './TemplateValidationPanel'
import type { BuiltValidationState } from '../utils/insightValidation'
import { FIELD_LABEL_SX,
         SECTION_HEADING_SX }         from './insightFormStyles'

interface InsightEditFormProps {
  mode          : 'create' | 'edit'
  value         : InsightCreateData
  onChange      : (patch: Partial<InsightCreateData>) => void
  /** The stored record — absent in create mode. */
  record       ?: InsightRecord
  /** The live version, for the modified highlight. */
  published    ?: InsightRecord
  /** Attributes the GenAI audit rows; optional because create has no pilot-saved
   *  record yet and the routes accept it as optional for exactly that reason. */
  pilotId      ?: string
  catalog      ?: InsightCatalog
  /**
   * Renders the record as a reference copy: no action row, no workflow
   * controls, no AI assistant, every input disabled.
   *
   * Used by the Published tab, which shows what production is serving right
   * now. Nothing there is editable **by construction** rather than by the
   * caller remembering to pass a no-op `onChange` — that record is not the one
   * a save would patch, so a reachable control would write the author's change
   * to the wrong version.
   */
  readOnly     ?: boolean
  isSaving      : boolean
  isDirty       : boolean
  saveError    ?: string
  /** Receives the validation state to persist — client-owned, stored verbatim. */
  onSaveDraft   : (validation?: BuiltValidationState) => void
  onSaveSubmit  : (validation?: BuiltValidationState) => void
  onUndo       ?: () => void
  onDiscard    ?: () => void
  /** The criteria builder — rendered above the template text, because every
   *  variable it constrains has to be referenced in that text. */
  criteriaSlot ?: ReactNode
  /** Reco tagging — the last content panel, above the validation tiles. */
  relatedSlot  ?: ReactNode
  /** DA-2392 injects the QA status control and publish actions here. */
  workflowSlot ?: ReactNode
}

export const InsightEditForm = ({
  mode,
  value,
  onChange,
  record,
  published,
  pilotId,
  catalog,
  readOnly,
  isSaving,
  isDirty,
  saveError,
  onSaveDraft,
  onSaveSubmit,
  onUndo,
  onDiscard,
  criteriaSlot,
  relatedSlot,
  workflowSlot,
}: InsightEditFormProps) => {

  const { rc } = useGlobalConfig()

  const [aiOpen, setAiOpen] = useState(false)

  /* Changing the template text means the stored verdict no longer describes it,
     so the author has to re-run Validate before saving. Mechanical only — see
     the note in TemplateValidationPanel on why semantic never blocks. */
  const [gate, setGate] = useState<ValidationGate>({
    requiresValidation : false, satisfied : false, semanticIsTheBlocker : false, built : undefined,
  })
  const blockedByValidation = gate.requiresValidation && !gate.satisfied

  const modified = useMemo(
    () => modifiedFields(value, published),
    [value, published],
  )

  const editStatus  = record?.editStatus
  const isPublished = editStatus === CONTENT_EDIT_STATUS.PUBLISHED
  const isDraft     = editStatus === CONTENT_EDIT_STATUS.DRAFT
  const isModified  = editStatus === CONTENT_EDIT_STATUS.MODIFIED

  return (
    <Box>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="flex-start" sx={{ mb : 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight : 600 }}>
              {mode === 'create'
                ? 'Create insight'
                : `${readOnly ? 'Published insight' : 'Edit insight'}: ${record?.insightId ?? ''}`}
            </Typography>
            {editStatus && (
              <Chip
                size="small"
                label={editStatus === CONTENT_EDIT_STATUS.MODIFIED ? 'Modified' : editStatus === CONTENT_EDIT_STATUS.PUBLISHED ? 'Published' : 'Draft'}
                color={isPublished ? 'success' : isModified ? 'warning' : 'default'}
              />
            )}
          </Stack>
          {record?.code && (
            <Typography variant="body2" sx={{ fontFamily : 'monospace', color : 'text.secondary' }}>
              {record.code}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* The Published tab has its own banner: the four state branches below
          describe where the EDITABLE copy stands, and two of them ("changed
          fields are highlighted", "modifications will create a new draft")
          would be read as instructions on a version nothing here can change. */}
      {readOnly && (
        <Alert severity="success" sx={{ mb : 2 }}>
          <strong>Published version.</strong> This is the content production is
          serving right now, shown read-only. Switch to the draft tab to make
          changes — they are saved against the draft and only reach this version
          when it is published.
        </Alert>
      )}

      {/* ── State banner — four branches, as on the reco edit screen ───── */}
      {mode === 'edit' && !readOnly && (
        isDraft ? (
          <Alert severity="warning" sx={{ mb : 2 }}>
            This is the draft version of the insight. It is not yet published and is
            not visible in production.
          </Alert>
        ) : isModified ? (
          <Alert severity="info" sx={{ mb : 2 }}>
            This is the modified version of the published insight. Changed fields are
            highlighted below.
          </Alert>
        ) : isPublished ? (
          <Alert severity="success" sx={{ mb : 2 }}>
            <strong>Viewing original content.</strong> This insight is currently in sync
            with the published version. Modifications will create a new draft.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb : 2 }}>
            No modifications have been made to this insight yet. Values are autofilled
            from the published version.
          </Alert>
        )
      )}

      {saveError && <Alert severity="error" sx={{ mb : 2 }}>{saveError}</Alert>}

      {/* Says WHY saving is unavailable. A disabled button with no explanation
          is indistinguishable from a broken one. */}
      {blockedByValidation && (
        <Alert severity={gate.semanticIsTheBlocker ? 'warning' : 'info'} sx={{ mb : 2 }}>
          {gate.semanticIsTheBlocker
            ? <>The variables and capping checks are green, but the <strong>semantic</strong> check
               is not. Only a TPM can save past a semantic verdict — ask one to review, or
               revise the wording and re-run <strong>Validate</strong>.</>
            : <>The template text changed — run <strong>Validate</strong> before saving, so the
               stored checks describe the new wording. All three must be green.</>}
        </Alert>
      )}

      {/* ── Action row ─────────────────────────────────────────────────── */}
      {/* Absent, not disabled, in the published view. A greyed Save on a
          reference copy invites the author to wonder what would happen if it
          were enabled; the answer is that it would patch the wrong record. */}
      {!readOnly && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb : 2 }}>
          {mode === 'edit' && (
            <>
              <Button
                size="small" startIcon={<RestoreIcon />}
                disabled={!isDirty || isSaving || !onUndo} onClick={onUndo}
              >
                Undo
              </Button>
              <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.discard.id}>
                <Button
                  size="small" color="error" startIcon={<DeleteIcon />}
                  disabled={isSaving || !onDiscard} onClick={onDiscard}
                >
                  Discard
                </Button>
              </PermissionGate>
            </>
          )}

          <Box sx={{ flexGrow : 1 }} />

          {workflowSlot}

          <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.save.id}>
            <Button
              variant="outlined"
              disabled={!isDirty || isSaving || blockedByValidation}
              onClick={() => onSaveDraft(gate.built)}
              startIcon={isSaving ? <CircularProgress size={14} /> : undefined}
            >
              {mode === 'create' ? 'Create draft' : 'Save draft'}
            </Button>
          </PermissionGate>

          <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.submit.id}>
            <Button
              variant="contained"
              disabled={!isDirty || isSaving || blockedByValidation}
              onClick={() => onSaveSubmit(gate.built)}
            >
              Save &amp; submit
            </Button>
          </PermissionGate>
        </Stack>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs : 12, lg : 7 }}>

          {/* ── Technical identification — derived, never editable ────── */}
          {mode === 'edit' && (
            <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb : 1.5 }}>
                <StorageIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={SECTION_HEADING_SX}>
                  TECHNICAL IDENTIFICATION
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ xs : 12, sm : 6 }}>
                  <TextField
                    fullWidth size="small" label="Insight ID" disabled
                    value={record?.insightId ?? ''}
                    helperText="Derived on create; immutable"
                    InputProps={{ sx : { fontFamily : 'monospace' } }}
                    sx={FIELD_LABEL_SX}
                  />
                </Grid>
                <Grid size={{ xs : 12, sm : 6 }}>
                  <TextField
                    fullWidth size="small" label="Code" disabled
                    value={record?.code ?? ''}
                    helperText="Unique per project, not globally"
                    InputProps={{ sx : { fontFamily : 'monospace' } }}
                    sx={FIELD_LABEL_SX}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
            <InsightClassificationForm
              value={value}
              onChange={onChange}
              catalog={catalog}
              /* Classification derives `code`, which is immutable, so it is
                 editable only while the record does not exist yet. */
              readOnly={mode === 'edit'}
              modified={modified}
            />
          </Paper>

          {criteriaSlot}

          <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
            <TemplateTextEditor
              value={value.templateString ?? ''}
              onChange={(next) => onChange({ templateString : next })}
              catalog={catalog}
              insightType={value.type}
              disabled={isSaving || readOnly}
              /* No highlight in the published view — there is nothing for it to
                 be modified relative to; it IS the baseline. */
              modified={!readOnly && modified.has('templateString')}
            />

            {/* AI Writing Assistant — present now that DA-2401 has shipped the
                endpoint. Gated on its own feature: proxypulse meters and audits
                these calls for cost, so a role without the grant should not see
                the button at all. Absent in the published view: a suggestion
                there would have nowhere to be applied. */}
            {!readOnly && (
              <>
                <Divider sx={{ my : 2 }} />
                <PermissionGate require={BFF_FEATURES.INSIGHTS.GENERATE_ALTERNATIVE}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    disabled={isSaving || !(value.templateString ?? '').trim()}
                    onClick={() => {
                      rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.AI_ASSISTANT_OPENED)
                      setAiOpen(true)
                    }}
                    sx={{ mt : 2 }}
                  >
                    AI Writing Assistant
                  </Button>
                </PermissionGate>
              </>
            )}

          </Paper>

          {/* Directly under the text and the assistant they judge. The tiles
              report on token validity, meaning, and whether every constrained
              variable is referenced in the text — all of which is about the
              sentence immediately above, so the verdict belongs next to it
              rather than after an unrelated relations panel. */}
          <PermissionGate require={BFF_FEATURES.INSIGHTS.VALIDATE_TEMPLATE_TEXT}>
            <Paper variant="outlined" sx={{ p : 2, mb : 2 }}>
              <TemplateValidationPanel
                templateString={value.templateString ?? ''}
                originalTemplateString={mode === 'create' ? undefined : (record?.templateString ?? '')}
                storedValidationState={record?.validationState}
                onGateChange={setGate}
                insight={value}
                catalog={catalog}
                pilotId={pilotId}
                insightDocumentId={record?.documentId}
              />
            </Paper>
          </PermissionGate>

          <AiWritingAssistantDialog
            open={aiOpen}
            templateString={value.templateString ?? ''}
            insightType={value.type}
            pilotId={pilotId}
            insightDocumentId={record?.documentId}
            onClose={() => setAiOpen(false)}
            /* Applied to templateString, not accumulated in alternativeVersions:
               that field is i18n-localized and `updateAll` cannot write localized
               content yet (DA-2369), so an accepted alternative parked there
               would be silently dropped. It flows through the normal diff/save
               path like any hand-typed edit. */
            onApply={(text) => onChange({ templateString : text })}
          />

          {/* Relations, not content — so it closes the form rather than sitting
              between the template text and the tiles that judge it.

              Injected as a slot because the two pages wire it two different
              ways: the edit page writes each pick straight through
              `tagRecommendations` and pushes the response into the store for
              its fresh `allowedTransitions`, while create has no documentId to
              tag yet and buffers the picks until after the POST. Owning that
              state here would mean this form knowing about both. */}
          {relatedSlot}
        </Grid>

        {/* ── Right rail ──────────────────────────────────────────────── */}
        <Grid size={{ xs : 12, lg : 5 }}>
          <Paper
            variant="outlined"
            sx={{ p : 3, position : 'sticky', top : 16, textAlign : 'center' }}
          >
            <VisibilityIcon color="disabled" />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt : 1 }}>
              <Typography variant="subtitle2" sx={SECTION_HEADING_SX}>
                PREVIEW
              </Typography>
              <Chip size="small" variant="outlined" label="Upcoming" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt : 1 }}>
              A live rendering of this insight against sample data is on its way.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default InsightEditForm
